import { BaseService } from "@/api/base-service";
import { buildQueryParams } from "@/utils/build-query-params";
import type {
  Event,
  EventCreate,
  EventUpdate,
  EventAthlete,
  EventAttendance,
  GetEventsParams,
  GetEventsResponse,
  UpdateMode,
} from "./types";

export class EventsService extends BaseService {
  static get instance() {
    return this.getInstance<EventsService>();
  }

  public async getEvents(params: GetEventsParams): Promise<GetEventsResponse> {
    const query = buildQueryParams({ ...params });
    const url = `/events${query ? `?${query}` : ""}`;

    return this.client.get<GetEventsResponse>(url);
  }

  public async getEvent(gymId: number, eventId: number): Promise<Event> {
    const response = await this.client.get<{ event: Event }>(
      `/gyms/${gymId}/events/${eventId}`,
    );

    return response.event;
  }

  public async deleteEvent(
    gymId: number,
    eventId: number,
    deleteMode?: UpdateMode,
  ): Promise<void> {
    return this.client.delete(
      `/gyms/${gymId}/events/${eventId}${deleteMode ? `?delete_mode=${deleteMode}` : ""}`,
    );
  }

  public async getEventAttendances(
    gymId: number,
    eventId: number,
  ): Promise<{ eventAttendances: EventAttendance[] }> {
    const url = `/gyms/${gymId}/events/${eventId}`;
    const response = await this.client.get<{ event: Event }>(url);
    return { eventAttendances: response.event.eventAttendances || [] };
  }

  public async addEventAttendance(
    gymId: number,
    eventId: number,
    athleteId: number,
    status: string,
    attendanceNotes?: string,
  ): Promise<void> {
    const url = `/gyms/${gymId}/events/${eventId}/event_attendances`;
    const body = {
      eventAttendance: {
        athleteId,
        status,
        attendanceNotes,
      },
    };
    await this.client.post(url, body);
  }

  public async updateEventAttendance(
    gymId: number,
    eventId: number,
    attendanceId: number,
    status: string,
    attendanceNotes?: string,
  ): Promise<void> {
    const url = `/gyms/${gymId}/events/${eventId}/event_attendances/${attendanceId}`;
    const body = {
      eventAttendance: {
        status,
        attendanceNotes,
      },
    };
    await this.client.patch(url, body);
  }

  public async deleteEventAttendance(
    gymId: number,
    eventId: number,
    attendanceId: number,
  ): Promise<void> {
    const url = `/gyms/${gymId}/events/${eventId}/event_attendances/${attendanceId}`;
    await this.client.delete(url);
  }

  public async getAvailableAthletes(
    gymId: number,
    eventId: number,
    search?: string,
    page?: number,
    perPage?: number,
  ) {
    const params: Record<string, string | number> = {};
    if (search) params.search = search;
    if (page) params.page = page;
    if (perPage) params.perPage = perPage;

    const query = buildQueryParams(params);
    const url = `/gyms/${gymId}/events/${eventId}/available_athletes${query ? `?${query}` : ""}`;

    return this.client.getWithHeaders<{ athletes: EventAthlete[] }>(url);
  }

  public async createEvent(gymId: number, data: EventCreate): Promise<Event> {
    const response = await this.client.post<{ event: Event }>(
      `/gyms/${gymId}/events`,
      {
        event: data,
      },
    );

    return response.event;
  }

  public async approveEvent(gymId: number, eventId: number): Promise<void> {
    await this.client.post(`/gyms/${gymId}/events/${eventId}/approve`, null);
  }

  public async rejectEvent(
    gymId: number,
    eventId: number,
    reason?: string,
  ): Promise<void> {
    await this.client.post(`/gyms/${gymId}/events/${eventId}/reject`, {
      eventApproval: { reason },
    });
  }

  public async updateEvent(
    gymId: string,
    id: string,
    data: EventUpdate,
    updateMode?: UpdateMode,
  ): Promise<Event> {
    const response = await this.client.put<{ event: Event }>(
      `/gyms/${gymId}/events/${id}${updateMode ? `?update_mode=${updateMode}` : ""}`,
      {
        event: data,
      },
    );

    return response.event;
  }
}
